import { Box, Link, Paper, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { MouseEvent } from 'react';
import type { MetricResult } from '../../types';
import { useMetricDrilldown } from '../../context/MetricDrilldownContext';
import { cardShellSx, getMetricDisplayName } from './rendererTypes';
import type { MetricRendererProps } from './rendererTypes';
import TileMetaActions from '../TileMetaActions';
import { hasTileMetaChrome, tileMetaTitlePad } from '../../utils/tileMeta';

type BreakdownItem = NonNullable<MetricResult['breakdown']>[number];

const isOthersRow = (item: BreakdownItem) =>
  item.color === 'grey' || /other/i.test(item.label);

const barFill = (item: BreakdownItem, theme: Theme) =>
  isOthersRow(item) ? theme.palette.grey[400] : theme.palette.primary.main;

const formatShareValue = (item: BreakdownItem) => {
  if (item.pct != null && item.value != null && item.value !== item.pct) {
    return `${item.value}  ${item.pct}%`;
  }
  if (item.pct != null) return `${item.pct}%`;
  return String(item.value ?? '');
};

function FooterRow({
  description,
  footerLink,
  metricId,
  divider,
  primary,
}: {
  description?: string;
  footerLink?: MetricResult['footerLink'];
  metricId?: string;
  divider: string;
  primary: string;
}) {
  const { openL2 } = useMetricDrilldown();

  if (!description && !footerLink) return null;

  const handleFooterClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (metricId) {
      openL2(metricId);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mt: 'auto',
        pt: 1.5,
        borderTop: `1px solid ${divider}`,
      }}
    >
      {description ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>
      ) : (
        <Box />
      )}
      {footerLink ? (
        <Link
          href={footerLink.href || '#'}
          underline="hover"
          onClick={handleFooterClick}
          sx={{
            color: primary,
            fontSize: 13,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          {footerLink.label} →
        </Link>
      ) : null}
    </Box>
  );
}

function ShareBar({ pct, color }: { pct: number; color: string }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height: 6,
        borderRadius: 1,
        backgroundColor: theme.palette.action.hover,
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: `${Math.min(100, pct)}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius: 1,
        }}
      />
    </Box>
  );
}

/** Design §4 rankedBreakdown: breakdown, footerLink.
 * `span === 'full'` → MODEL / UNITS / SHARE table; otherwise compact ranked list. */
export default function RankedBreakdownRenderer({
  config,
  data,
  span,
  metricId,
}: MetricRendererProps) {
  const theme = useTheme();
  const title = getMetricDisplayName(data, config);
  const items = data.breakdown ?? [];
  const isTable = span === 'full';

  if (isTable) {
    return (
      <Paper elevation={0} sx={cardShellSx(theme.palette.divider, theme.palette.background.paper)}>
        <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
        <Box
          sx={{
            display: 'grid',
            // Compact label cols + bar fills remaining — avoids a huge gap after MODEL
            gridTemplateColumns: 'minmax(7rem, 12rem) 4.5rem 4.5rem minmax(0, 1fr)',
            columnGap: { xs: 1.5, sm: 2.5 },
            rowGap: 1.5,
            alignItems: 'center',
            width: '100%',
            flex: 1,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em' }}
          >
            MODEL
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em' }}
          >
            UNITS
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em' }}
          >
            SHARE
          </Typography>
          <Box />

          {items.map(item => {
            const muted = isOthersRow(item);
            const fill = barFill(item, theme);
            return (
              <Box key={item.label} sx={{ display: 'contents' }}>
                <Typography
                  variant="body2"
                  sx={{ color: muted ? 'text.secondary' : 'text.primary', fontWeight: muted ? 500 : 600 }}
                  noWrap
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: muted ? 'text.secondary' : 'text.primary', fontWeight: 600 }}
                >
                  {item.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: muted ? 'text.secondary' : 'text.primary', fontWeight: 600 }}
                >
                  {item.pct != null ? `${item.pct}%` : '—'}
                </Typography>
                {item.pct != null ? <ShareBar pct={item.pct} color={fill} /> : <Box />}
              </Box>
            );
          })}
        </Box>

        <FooterRow
          description={data.description}
          footerLink={data.footerLink}
          metricId={metricId}
          divider={theme.palette.divider}
          primary={theme.palette.primary.main}
        />
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={cardShellSx(theme.palette.divider, theme.palette.background.paper)}>
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {data.overline ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
            mb: 0.5,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          {data.overline}
        </Typography>
      ) : null}
      {title ? (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1.5,
            lineHeight: 1.3,
            pr: !data.overline && hasTileMetaChrome(data.meta) ? 7 : 0,
          }}
        >
          {title}
        </Typography>
      ) : null}

      <Box sx={{ flex: 1 }}>
        {items.map(item => {
          const muted = isOthersRow(item);
          const fill = barFill(item, theme);
          return (
            <Box key={item.label} sx={{ mb: 1.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: muted ? 'text.secondary' : 'text.primary' }}
                  noWrap
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: muted ? 'text.secondary' : 'text.primary' }}
                >
                  {formatShareValue(item)}
                </Typography>
              </Box>
              {item.pct != null ? <ShareBar pct={item.pct} color={fill} /> : null}
            </Box>
          );
        })}
      </Box>

      <FooterRow
        description={data.description}
        footerLink={data.footerLink}
        metricId={metricId}
        divider={theme.palette.divider}
        primary={theme.palette.primary.main}
      />
    </Paper>
  );
}
